import type {
  AnalysisType,
  AnalyzeResponse,
  EventActivationMeta,
  EvinDocument,
  LabelUsage,
  SocialPostMeta,
  SuggestFixRequest,
  SuggestedFix,
  ServerAnalysisRecord,
  AnalysesListFilters,
  AnalysesStats,
  AnalysisActionTaken,
  ApiKey,
  CreatedApiKey,
  AdminOrganization,
  AdminPromoCode,
  PromoCohortReport,
  BetaCreditsMode,
  PromoCodeInput,
  PromoCodePublicInfo,
  PromoRedeemStatus,
  PromoRedeemSuccess,
  AnalysisFeedbackPayload,
  NiceClass,
  PlanType,
  TrademarkSearchResult,
  BillingInvoice,
} from '../../shared/types';
import { pb, authHeaders, setMfaSession } from './pocketbase';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ── Auth-aware fetch ───────────────────────────────────

// Le jeton PocketBase est lu directement dans l'authStore (localStorage), tenu
// à jour par le SDK, et la session MFA à côté (cf. services/pocketbase.ts).
// Sans jeton valide on n'envoie pas d'en-tête Authorization : c'est le serveur
// qui répond 401. La fonction reste `async` : elle est attendue par tous les
// appelants.
async function getAuthHeaders(): Promise<Record<string, string>> {
  return authHeaders();
}

async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const authHeaders = await getAuthHeaders();
  const headers = new Headers(options.headers);
  for (const [k, v] of Object.entries(authHeaders)) {
    headers.set(k, v);
  }
  return fetch(url, { ...options, headers });
}

// ── Analysis ────────────────────────────────────────────

export interface AnalysisProgressEvent {
  stage: string;
  label: string;
  progress: number; // 0-100
}

export async function analyzeContent(
  type: AnalysisType,
  options: {
    textContent?: string;
    context?: string;
    images?: File[];
    social?: SocialPostMeta;
    labelUsage?: LabelUsage;
    event?: EventActivationMeta;
  },
  onProgress?: (event: AnalysisProgressEvent) => void,
): Promise<AnalyzeResponse> {
  const formData = new FormData();
  formData.append('type', type);

  if (options.textContent) {
    formData.append('textContent', options.textContent);
  }
  if (options.context) {
    formData.append('context', options.context);
  }
  if (options.images) {
    for (const img of options.images) {
      formData.append('images', img);
    }
  }
  if (options.labelUsage) {
    formData.append('labelUsage', options.labelUsage);
  }
  if (options.event) {
    formData.append('event', JSON.stringify(options.event));
  }
  if (options.social) {
    formData.append('socialMode', options.social.mode);
    if (options.social.format) formData.append('format', options.social.format);
    if (options.social.platform) formData.append('platform', options.social.platform);
    if (options.social.caption) formData.append('caption', options.social.caption);
    if (options.social.hashtags?.length)
      formData.append('hashtags', JSON.stringify(options.social.hashtags));
    if (options.social.mentions?.length)
      formData.append('mentions', JSON.stringify(options.social.mentions));
  }

  const authHeaders = await getAuthHeaders();

  // Mode SSE : si onProgress est fourni, on demande un flux text/event-stream
  // au backend et on parse les events au fil de l'eau. Le résultat final
  // arrive dans un event 'done'. Si on ne passe pas onProgress, on retombe
  // sur le mode JSON synchrone d'origine (utilisé par BBCA, MCP, etc.).
  if (onProgress) {
    const headers = new Headers(authHeaders);
    headers.set('Accept', 'text/event-stream');
    const res = await fetch(`${API_URL}/api/evin/analyze`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error ?? `Erreur ${res.status}` };
    }
    return parseSseAnalyzeStream(res.body, onProgress);
  }

  // Mode JSON classique
  const res = await fetch(`${API_URL}/api/evin/analyze`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { success: false, error: data.error ?? `Erreur ${res.status}` };
  }

  return res.json();
}

// Parse un flux SSE `event: NAME\ndata: JSON\n\n` et redirige les progress
// events au callback ; renvoie le `done` (ou `error`) final décodé.
async function parseSseAnalyzeStream(
  body: ReadableStream<Uint8Array>,
  onProgress: (event: AnalysisProgressEvent) => void,
): Promise<AnalyzeResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let final: AnalyzeResponse | null = null;

  // Format SSE : un event = bloc séparé par `\n\n`. Chaque bloc contient
  // des lignes `event: X` et `data: Y` (Y peut être multi-ligne mais ici
  // on émet toujours un seul `data:` par event).
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let eventName = 'message';
      let dataLine = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLine = line.slice(5).trim();
      }
      if (!dataLine) continue;

      try {
        const payload = JSON.parse(dataLine);
        if (eventName === 'progress') {
          onProgress(payload as AnalysisProgressEvent);
        } else if (eventName === 'done') {
          final = payload as AnalyzeResponse;
        } else if (eventName === 'error') {
          final = payload as AnalyzeResponse;
        }
      } catch (e) {
        console.warn('[SSE] Failed to parse event:', eventName, dataLine.slice(0, 80), e);
      }
    }
  }

  if (!final) {
    return { success: false, error: 'Connexion interrompue avant la fin de l\'analyse.' };
  }
  return final;
}

// ── LLM quality feedback (Langfuse) ─────────────────────

export async function submitAnalysisFeedback(
  payload: AnalysisFeedbackPayload,
): Promise<void> {
  const res = await authFetch(`${API_URL}/api/evin/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `Erreur ${res.status}`);
  }
}

// ── Suggestion (fix auto) ───────────────────────────────

export async function suggestFix(req: SuggestFixRequest): Promise<SuggestedFix> {
  const res = await authFetch(`${API_URL}/api/evin/suggest-fix`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `Erreur ${res.status}`);
  }
  const data = await res.json();
  return data.suggestion as SuggestedFix;
}

// ── Server-side analyses history ────────────────────────

export async function listServerAnalyses(
  filters: AnalysesListFilters = {},
): Promise<ServerAnalysisRecord[]> {
  const url = new URL(`${API_URL}/api/evin/analyses`);
  if (filters.period) url.searchParams.set('period', filters.period);
  if (filters.verdict) url.searchParams.set('verdict', filters.verdict);
  if (filters.platform) url.searchParams.set('platform', filters.platform);
  if (filters.socialMode) url.searchParams.set('socialMode', filters.socialMode);
  if (filters.analysisType) url.searchParams.set('analysisType', filters.analysisType);
  if (filters.userTag) url.searchParams.set('userTag', filters.userTag);
  if (filters.actionTaken) url.searchParams.set('actionTaken', filters.actionTaken);
  const res = await authFetch(url.toString());
  if (!res.ok) throw new Error('Échec du chargement de l\'historique');
  const data = await res.json();
  return data.analyses ?? [];
}

// B1 — poll d'un job d'analyse par son id (réattachement après refresh/fermeture
// d'onglet : on suit running → done/error côté serveur).
export async function getServerAnalysis(id: string): Promise<ServerAnalysisRecord | null> {
  const res = await authFetch(`${API_URL}/api/evin/analyses/${id}`);
  if (!res.ok) return null;
  const data = await res.json();
  return (data.analysis ?? null) as ServerAnalysisRecord | null;
}

export async function fetchServerAnalysesStats(
  period: '7d' | '30d' | '90d' | 'all' = '30d',
): Promise<AnalysesStats> {
  const res = await authFetch(
    `${API_URL}/api/evin/analyses/stats?period=${period}`,
  );
  if (!res.ok) throw new Error('Échec du chargement des statistiques');
  const data = await res.json();
  return data.stats;
}

export async function updateServerAnalysis(
  id: string,
  patch: {
    actionTaken?: AnalysisActionTaken;
    userTag?: string | null;
    publishedAt?: string | null;
    title?: string | null;
    suggestedFix?: string | null;
  },
): Promise<void> {
  const res = await authFetch(`${API_URL}/api/evin/analyses/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Mise à jour échouée');
}

export async function deleteServerAnalysis(id: string): Promise<void> {
  const res = await authFetch(`${API_URL}/api/evin/analyses/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Suppression échouée');
}

// ── Documents ───────────────────────────────────────────

export async function listDocuments(
  category?: string,
): Promise<EvinDocument[]> {
  const url = new URL(`${API_URL}/api/evin/documents`);
  if (category) url.searchParams.set('category', category);

  const res = await authFetch(url.toString());
  const data = await res.json();
  return data.documents ?? [];
}

// ── Stripe / Billing ───────────────────────────────────

export async function createCheckoutSession(
  plan: 'pro' | 'premium',
  interval: 'month' | 'year' = 'month',
): Promise<{ url: string }> {
  const res = await authFetch(`${API_URL}/api/stripe/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan, interval }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `Erreur ${res.status}`);
  }
  return res.json();
}

export async function createBundleCheckout(
  bundleId: string,
): Promise<{ url: string }> {
  const res = await authFetch(`${API_URL}/api/stripe/checkout-bundle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bundleId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `Erreur ${res.status}`);
  }
  return res.json();
}

/** Le pack payé dans cette Checkout Session est-il crédité ? (lecture seule,
 *  aucun appel à Stripe : le serveur cherche la session dans org_credits). */
export async function fetchCheckoutCredited(sessionId: string): Promise<boolean> {
  const res = await authFetch(
    `${API_URL}/api/stripe/checkout-status?session_id=${encodeURIComponent(sessionId)}`,
  );
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const data = (await res.json()) as { credited?: boolean };
  return data.credited === true;
}

/** Factures Stripe de l'org (abonnements et packs), les plus récentes d'abord. */
export async function fetchInvoices(): Promise<BillingInvoice[]> {
  const res = await authFetch(`${API_URL}/api/stripe/invoices`);
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const data = (await res.json()) as { invoices?: BillingInvoice[] };
  return data.invoices ?? [];
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const res = await authFetch(`${API_URL}/api/stripe/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to open billing portal');
  return res.json();
}

// ── Admin (owner dashboard) ────────────────────────────

export async function fetchUsageSummary(days = 30): Promise<Record<string, unknown>> {
  const res = await authFetch(`${API_URL}/api/admin/usage/summary?days=${days}`);
  if (!res.ok) throw new Error('Failed to fetch usage summary');
  return res.json();
}

export async function fetchDailyUsage(days = 30): Promise<Record<string, unknown>> {
  const res = await authFetch(`${API_URL}/api/admin/usage/daily?days=${days}`);
  if (!res.ok) throw new Error('Failed to fetch daily usage');
  return res.json();
}

export async function fetchTopUsers(days = 30): Promise<Record<string, unknown>> {
  const res = await authFetch(`${API_URL}/api/admin/usage/top-users?days=${days}`);
  if (!res.ok) throw new Error('Failed to fetch top users');
  return res.json();
}

export async function fetchRevenue(days = 30): Promise<Record<string, unknown>> {
  const res = await authFetch(`${API_URL}/api/admin/revenue?days=${days}`);
  if (!res.ok) throw new Error('Failed to fetch revenue');
  return res.json();
}

export async function fetchDemographics(): Promise<{
  total: number;
  profilesCompleted: number;
  byJobTitle: Record<string, number>;
  byIndustry: Record<string, number>;
}> {
  const res = await authFetch(`${API_URL}/api/admin/demographics`);
  if (!res.ok) throw new Error('Failed to fetch demographics');
  return res.json();
}

// ── Profile ────────────────────────────────────────────

// ── MFA — TOTP (application d'authentification), vérifié par le serveur ───
// Remplace `supabase.auth.mfa.*`. Le secret ne quitte jamais le serveur autrement
// que par le QR code affiché à l'activation.

async function mfaPost<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const res = await authFetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Opération 2FA échouée');
  }
  return res.json();
}

/** Démarre l'activation : QR code (PNG data URL) + secret à saisir à la main. */
export function startMfaEnrollment(): Promise<{ qrCode: string; secret: string; otpauthUrl: string }> {
  return mfaPost('/api/auth/mfa/enroll');
}

/** Confirme l'activation avec un premier code valide et conserve la session MFA. */
export async function confirmMfaEnrollment(code: string): Promise<{ success: boolean }> {
  const res = await mfaPost<{ success: boolean; mfaSession?: string }>('/api/auth/mfa/enroll/confirm', { code });
  if (res.mfaSession) setMfaSession(res.mfaSession);
  return res;
}

/** Retire la 2FA — un code valide est exigé, même connecté. */
export function disableMfa(code: string): Promise<{ success: boolean }> {
  return mfaPost('/api/auth/mfa/disable', { code });
}

// ── RGPD — Post-OAuth terms acceptance ─────────────────

export async function acceptTerms(version: string): Promise<{ success: boolean }> {
  const res = await authFetch(`${API_URL}/api/auth/accept-terms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? 'Acceptation échouée');
  }
  return res.json();
}

export async function updateProfile(
  data: { displayName?: string; jobTitle?: string; industry?: string },
): Promise<{ success: boolean }> {
  const res = await authFetch(`${API_URL}/api/auth/profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Mise à jour échouée');
  }
  return res.json();
}

export async function updateOrgName(name: string): Promise<{ success: boolean }> {
  const res = await authFetch(`${API_URL}/api/auth/org`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? 'Mise à jour échouée');
  }
  return res.json();
}

export async function uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
  const formData = new FormData();
  formData.append('avatar', file);
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/auth/avatar`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? 'Upload échoué');
  }
  return res.json();
}

export async function deleteAvatar(): Promise<void> {
  const res = await authFetch(`${API_URL}/api/auth/avatar`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Suppression échouée');
}

export async function fetchUsageStats(): Promise<{
  monthlyAnalyses: number;
  totalAnalyses: number;
  byType: Record<string, number>;
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  creditBalance: number;
  plan: string;
}> {
  const res = await authFetch(`${API_URL}/api/auth/usage`);
  if (!res.ok) throw new Error('Failed to fetch usage stats');
  return res.json();
}

// ── RGPD — Data portability (Art. 20) ──────────────────

export async function exportPersonalData(): Promise<void> {
  const res = await authFetch(`${API_URL}/api/auth/export`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? 'Export échoué');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `evinly-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── RGPD — Right to erasure (Art. 17) ──────────────────

export async function deleteAccount(confirmation: string): Promise<{ success: boolean; message: string }> {
  const res = await authFetch(`${API_URL}/api/auth/me`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Suppression échouée');
  }
  return res.json();
}

// ── Health ──────────────────────────────────────────────

export async function checkHealth(): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_URL}/health`);
  return res.json();
}

// ── API Keys ────────────────────────────────────────────

export async function listApiKeys(): Promise<ApiKey[]> {
  const res = await authFetch(`${API_URL}/api/auth/api-keys`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Échec du chargement des clés API');
  }
  const json = await res.json();
  return json.apiKeys ?? [];
}

export async function createApiKey(name: string): Promise<CreatedApiKey> {
  const res = await authFetch(`${API_URL}/api/auth/api-keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Création de la clé API échouée');
  }
  const json = await res.json();
  return json.apiKey;
}

export async function revokeApiKey(id: string): Promise<{ success: boolean }> {
  const res = await authFetch(`${API_URL}/api/auth/api-keys/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Révocation de la clé API échouée');
  }
  return res.json();
}

// ── Admin: organizations & feature toggles ─────────────

export async function listAdminOrganizations(): Promise<AdminOrganization[]> {
  const res = await authFetch(`${API_URL}/api/admin/orgs`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Chargement des organisations échoué');
  }
  const json = await res.json();
  return json.organizations ?? [];
}

export async function setOrgApiAccess(
  orgId: string,
  enabled: boolean,
): Promise<AdminOrganization> {
  const res = await authFetch(`${API_URL}/api/admin/orgs/${orgId}/api-access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Modification du flag API échouée');
  }
  const json = await res.json();
  return json.organization;
}

/** Change le plan d'une org (super-admin). `beta` provisionne 30 analyses et
 *  remet le compteur à 0 ; `partner` donne l'accès illimité offert. Pour régler
 *  un autre volume bêta sans écraser le compteur → `setOrgBetaCredits`.
 *  Voir POST /api/admin/orgs/:id/plan. */
export async function setOrgPlan(
  orgId: string,
  plan: PlanType,
): Promise<AdminOrganization> {
  const res = await authFetch(`${API_URL}/api/admin/orgs/${orgId}/plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Changement de plan échoué');
  }
  const json = await res.json();
  return json.organization;
}

/** Règle le pool d'analyses bêta d'une org (super-admin).
 *  `set` → le restant devient exactement `amount` ; `add` → ajoute `amount`
 *  (négatif pour retirer). Bascule l'org en `beta` si elle est encore `free`,
 *  et ne remet jamais le compteur d'analyses consommées à 0.
 *  Voir POST /api/admin/orgs/:id/beta-credits. */
export async function setOrgBetaCredits(
  orgId: string,
  mode: BetaCreditsMode,
  amount: number,
): Promise<AdminOrganization> {
  const res = await authFetch(`${API_URL}/api/admin/orgs/${orgId}/beta-credits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode, amount }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Modification des analyses bêta échouée');
  }
  const json = await res.json();
  return json.organization;
}

// ── Codes promo (cartes de visite salon) ───────────────

/** Ce que vaut un code, avant même d'avoir un compte (endpoint public).
 *  Ne lève jamais : un lookup en échec ne doit pas bloquer l'inscription,
 *  l'écran affiche simplement le formulaire sans bandeau d'offre. */
export async function fetchPromoInfo(code: string): Promise<PromoCodePublicInfo> {
  const fallback: PromoCodePublicInfo = { code, label: null, analyses: null, valid: false };
  try {
    const res = await fetch(`${API_URL}/api/promo/${encodeURIComponent(code)}`);
    if (!res.ok) return fallback;
    return await res.json();
  } catch {
    return fallback;
  }
}

/** Erreur d'échange portant le statut serveur, pour distinguer un refus
 *  DÉFINITIF (code expiré, déjà utilisé) d'un incident passager (réseau, 500).
 *  Le hook s'en sert pour décider s'il oublie le code ou s'il réessaiera. */
export class PromoRedeemError extends Error {
  constructor(message: string, readonly status: PromoRedeemStatus | 'network' | 'unknown') {
    super(message);
    this.name = 'PromoRedeemError';
  }
}

/** Échange le code sur l'organisation de l'utilisateur connecté. */
export async function redeemPromoCode(
  code: string,
  source: 'qr' | 'signup' | 'manual' = 'manual',
): Promise<PromoRedeemSuccess> {
  let res: Response;
  try {
    res = await authFetch(`${API_URL}/api/promo/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, source }),
    });
  } catch {
    throw new PromoRedeemError('Connexion perdue. Réessayez dans un instant.', 'network');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({} as { message?: string; status?: PromoRedeemStatus }));
    throw new PromoRedeemError(
      err.message ?? "L'échange du code a échoué.",
      err.status ?? 'unknown',
    );
  }
  return await res.json();
}

/** Liste les codes promo et leurs compteurs (super-admin). */
export async function fetchAdminPromoCodes(): Promise<AdminPromoCode[]> {
  const res = await authFetch(`${API_URL}/api/admin/promo-codes`);
  if (!res.ok) throw new Error('Chargement des codes promo échoué');
  const json = await res.json();
  return json.codes ?? [];
}

/** Crée ou met à jour un code promo (super-admin). Sert notamment à prolonger
 *  une campagne pour un salon suivant sans réimprimer les cartes. */
export async function saveAdminPromoCode(input: PromoCodeInput): Promise<AdminPromoCode> {
  const res = await authFetch(`${API_URL}/api/admin/promo-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Enregistrement du code échoué');
  }
  const json = await res.json();
  return json.promoCode;
}

/** Suspend ou réactive une organisation (super-admin). Réversible. */
export async function setOrgSuspended(
  orgId: string,
  suspended: boolean,
  reason?: string,
): Promise<AdminOrganization> {
  const res = await authFetch(`${API_URL}/api/admin/orgs/${orgId}/suspend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suspended, reason }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Changement de suspension échoué');
  }
  const json = await res.json();
  return json.organization;
}

/** Efface le compte propriétaire d'une organisation (super-admin).
 *  IRRÉVERSIBLE — réutilise la routine d'effacement RGPD. */
export async function deleteAdminOrg(orgId: string): Promise<void> {
  const res = await authFetch(`${API_URL}/api/admin/orgs/${orgId}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Suppression échouée');
  }
}

/** Purge le registre des échanges d'un code et remet son compteur à 0
 *  (super-admin). Les comptes concernés redeviennent éligibles au code ;
 *  les analyses déjà créditées, elles, ne sont pas reprises. */
export async function resetAdminPromoCode(code: string): Promise<AdminPromoCode> {
  const res = await authFetch(`${API_URL}/api/admin/promo-codes/${encodeURIComponent(code)}/reset`, {
    method: 'POST',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Remise à zéro échouée');
  }
  const json = await res.json();
  return json.promoCode;
}

/** Supprime un code promo désactivé (super-admin). Refusé (409) tant que le
 *  code est actif ; le registre des échanges, lui, est conservé. */
export async function deleteAdminPromoCode(code: string): Promise<void> {
  const res = await authFetch(`${API_URL}/api/admin/promo-codes/${encodeURIComponent(code)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Suppression du code échouée');
  }
}

/** Rapport de cohorte d'un code promo (super-admin). */
export async function fetchPromoReport(code: string): Promise<PromoCohortReport> {
  const res = await authFetch(`${API_URL}/api/admin/promo-codes/${encodeURIComponent(code)}/report`);
  if (!res.ok) throw new Error('Chargement du rapport échoué');
  return await res.json();
}

// ── Trademark availability (super-admin only) ──────────

export interface TrademarkProviderStatus {
  inpi: { configured: boolean; message?: string };
  euipo: { configured: boolean; message?: string; env?: 'sandbox' | 'production' };
}

export async function getTrademarkStatus(): Promise<TrademarkProviderStatus> {
  const res = await authFetch(`${API_URL}/api/trademark/status`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Status API marque indisponible');
  }
  return res.json();
}

export async function searchTrademark(
  brandName: string,
  niceClasses: NiceClass[],
): Promise<TrademarkSearchResult> {
  const res = await authFetch(`${API_URL}/api/trademark/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brandName, niceClasses }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? err.error ?? 'Recherche marque échouée');
  }
  return res.json();
}
